import { useEffect } from 'react'
import useSWR from 'swr'
import { apiGetPlanFeatures } from '@/services/BillingService'
import { usePlanActivationStore } from '@/store/planActivationStore'
import { revalidateBilling } from '@/views/settings/Billing/utils/choosePlan'
import type { PlanFeaturesResponse } from '@/@types/trackheros'

// How long to wait for Paddle's webhook before saying it is taking a while.
// Not a failure: the hourly reconcile delivers the plan even if the webhook
// never arrives.
const ACTIVATION_PATIENCE_MS = 60_000
const POLL_MS = 2_000

/**
 * Ends the wait `choosePlan` starts after a Paddle checkout.
 *
 * Mounted once, beside the quota dialog, because the wait outlives the page
 * that started it — onboarding pays and goes straight to the dashboard, and the
 * drawer's plan item is only mounted on wide screens. While a plan is pending
 * it polls `/billing/features/` (any member may read it, and it is the cheapest
 * billing read); when the plan arrives it refetches every billing key, so the
 * summary, the gates and the drawer all move together.
 *
 * Renders nothing.
 */
const PlanActivationWatcher = () => {
    const pending = usePlanActivationStore((state) => state.pending)
    const stalled = usePlanActivationStore((state) => state.stalled)
    const settle = usePlanActivationStore((state) => state.settle)
    const stall = usePlanActivationStore((state) => state.stall)

    const waitingFor = pending ?? stalled

    const { data } = useSWR<PlanFeaturesResponse>(
        waitingFor ? ['billing', 'features'] : null,
        () => apiGetPlanFeatures(),
        {
            refreshInterval: pending ? POLL_MS : 0,
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    )

    const arrived = Boolean(waitingFor) && data?.plan.code === waitingFor
    useEffect(() => {
        if (!arrived) return
        settle()
        void revalidateBilling()
    }, [arrived, settle])

    useEffect(() => {
        if (!pending) return
        const timer = window.setTimeout(stall, ACTIVATION_PATIENCE_MS)
        return () => window.clearTimeout(timer)
    }, [pending, stall])

    return null
}

export default PlanActivationWatcher
